// TL-1/TL-2: owns the active tool, feeds it snapped pointer events, routes keys and
// Measurements input, and renders tool previews + snap markers via the viewport delegates.
import { type Vec } from "../core/geometry";
import type { DocStore } from "../doc/store";
import { defaultActiveLayerId } from "../doc/document";
import type { Style } from "../doc/entities";
import { makeDeleteCommand, makeGroupCommand, makeUngroupCommand, type Command } from "../doc/commands";
import { SelectionSet } from "../engine/selection";
import { SnapEngine, type SnapResult } from "../engine/snap";
import { gridStep } from "../engine/renderBg";
import { worldToScreen } from "../engine/camera";
import { applyWorldTransform, type RenderView } from "../engine/renderEntities";
import { hitTest, HIT_TOL_PX } from "../engine/hitTest";
import { entityBounds } from "../engine/bounds";
import { pointInImage } from "../engine/imageGeom";
import type { PointerMods, Viewport } from "../engine/viewport";
import { outlineEntity, SelectTool } from "./select";
import { LineTool, clearReadoutBoxes, drawnReadoutBoxes } from "./line";
import { placeCursorTip } from "./tipPlace";
import { RectTool } from "./rect";
import { CircleTool } from "./circle";
import { ArcTool } from "./arc";
import { CalibrateTool } from "./calibrate";
import { PerspectiveTool } from "./perspective";
import { StraightenTool } from "./straighten";
import { FreehandTool, LabelTool, TapeTool, TextTool } from "./annotate";
import { DimensionTool } from "./dimension";
import { GridTool } from "./gridTool";
import { MarkerTool, type MarkerKind } from "./marker";
import { CropTool } from "./crop";
import { isLocked } from "./entityOps";
import { numberShapeGeometry, numberShapeProblem, type NumberShape } from "./byNumbers";
import { PlaceTool } from "./place";
import {
  combine,
  DEFAULT_PIPE_PREFS,
  drawPorts,
  freePortNear,
  joinPatches,
  looksLikeFitting,
  looksLikePipe,
  ownerName,
  PipeTool,
  safePorts,
  unjoinPatches,
  type PipePrefs
} from "./pipeTool";
import { endOptions, placeFittingAtEnd, type EndOption, type PipeEnd } from "../pipes/terminate";
import { insertFittingCommand, insertOptions, pipeAxis, type InsertOption } from "../pipes/insert";
import { butterflyCommand } from "../pipes/butterfly";
import { DEFAULT_WALL_PREFS, WallTool, type WallPrefs } from "./wallTool";
import { DOOR_WIDTH, DOOR_WORDS, wallMetaOf, type DoorKind, type WallDoor } from "../walls/wall";
import { addDoorCommand, doorAt, removeDoorCommand, setSegmentLengthCommand, setWallCommand, splitWallCommand, updateDoorCommand, wallCornerAt, wallMeetings, wallSegmentAt } from "../walls/edit";
import { operatorSizes, type ButterflySpec } from "../library/butterflyValves";
import { resizeFittingCommand } from "../pipes/resizeFitting";
import { lintPotCommand, placedLintPot, type LintPotEdit } from "../pipes/lintPot";
import { lintPotName, makeLintPotItem, type LintPotSpec } from "../library/lintPots";
import { drawnSizes, pumpItemFor, type PumpInfo } from "../library/pumps";
import { redrawPumpCommand } from "../pipes/pumpRedraw";
import { setPortSizeCommand } from "../pipes/portSize";
import type { Port } from "../pipes/api";
import { extendFromPortCommand, pipeEnds, pipeMetaOf, portsOf, setPipePatches, type PipeEndStyle, type PipeMeta } from "../pipes/api";
import type { LibraryItem } from "../library/library";
import { copyEntities as copyForMove, pasteCommand as pasteForMove } from "./editOps";
import { moveCommand, rotatePatches } from "./entityOps";
import { setPivotPatch, turnPoint } from "./pivot";
import {
  alignPatches,
  besidePatches,
  centrePatches,
  distributePatches,
  movableIds,
  objectBox,
  spacePatches,
  spaceUnits,
  type AlignEdge,
  type Side,
  type SpaceMode
} from "./align";
import { newId } from "../doc/document";
import type { CircleEntity, PolylineEntity } from "../doc/entities";
import {
  addVertexCommand,
  applyStyleCommand,
  copyEntities,
  moveToLayerCommand,
  pasteCommand,
  removeVertexCommand,
  setLockedCommand,
  simplifyCommand,
  toggleClosedCommand,
  vertexAt,
  zOrderCommand,
  type ClipboardData
} from "./editOps";
import type { DoorPick, MeasureShape, TextEditRequest, Tool, ToolCtx, ToolId, ToolPointerEvent } from "./types";

/** ANN-1: what the UI needs to overlay the inline text editor. */
export interface TextEditState {
  world: Vec; // authoritative — the UI re-projects on camera changes
  fontMm: number;
  screen: Vec;
  fontPx: number; // ≥ 11 (EC-ANN-1)
  initial: string;
  multiline: boolean;
}

export interface MenuItem {
  label: string;
  action?: () => void;
  disabled?: boolean;
  separator?: boolean; // renders a divider ABOVE this item
  children?: MenuItem[];
}

export interface ToolUiState {
  toolId: ToolId;
  hint: string;
  measureShape: MeasureShape;
  measureGhost: string;
  snapKind: string; // current snap marker label for the status bar (SNP-6)
  snapEnabled: boolean;
  orthoSticky: boolean;
  gridSnap: boolean;
  /** An open typed-number box over the drawing: M = move by, Q = rotate by, B = save to library. */
  prompt: SelectionPrompt | null;
}

export type SelectionPrompt = "move" | "rotate" | "save";

/** UI-5 preferences — persisted as one settings blob by the composition root. */
export interface Prefs {
  theme: "dark" | "light";
  polar45: boolean;
  freehandTolPx: number; // RDP smoothing: 0.5 low / 0.75 medium / 1.5 high
  dimImagesWhileDrawing: boolean;
  keymap: Record<string, ToolId>; // key → tool (UI-5 keybinding editor)
  coachDone: string[]; // UI-6 one-time coach marks
  /** F2: highlighter creation defaults — size (screen px), colour, opacity. */
  highlighter: { width: number; color: string; opacity: number };
  /** S2: push to Eden automatically after autosave settles (silent, checksum-skipped). */
  edenAutoSync: boolean;
  /** S3: poll Eden for server-side changes (focus + 90s while visible) — live status. */
  edenLiveUpdates: boolean;
  /** Pipe tool: size of a new pipe and the look of its free ends (owner, 2026-10-06). */
  pipe: PipePrefs;
  /** Wall tool (3.20.0): thickness, the line you draw, walls or room, fill. */
  wall: WallPrefs;
}

export const DEFAULT_KEYMAP: Record<string, ToolId> = {
  v: "select",
  l: "line",
  r: "rect",
  c: "circle",
  a: "arc",
  k: "calibrate",
  t: "text",
  n: "label",
  d: "dimension",
  g: "grid",
  f: "freehand",
  p: "pipe"
};

export const DEFAULT_PREFS: Prefs = {
  theme: "dark",
  polar45: false,
  freehandTolPx: 0.75,
  dimImagesWhileDrawing: false,
  keymap: DEFAULT_KEYMAP,
  coachDone: [],
  highlighter: { width: 4, color: "#e0d34d", opacity: 0.5 },
  edenAutoSync: false,
  edenLiveUpdates: true,
  pipe: { ...DEFAULT_PIPE_PREFS },
  wall: { ...DEFAULT_WALL_PREFS }
};

const DRAW_TOOLS = new Set<ToolId>([
  "line",
  "pipe",
  "wall",
  "rect",
  "circle",
  "arc",
  "freehand",
  "text",
  "label",
  "dimension",
  "grid",
  "marker"
]);

/** Per-tool default canvas cursor; unlisted tools fall back to crosshair. */
const TOOL_CURSOR: Partial<Record<ToolId, string>> = {
  select: "default",
  text: "text",
  perspective: "default"
};

export class ToolManager {
  readonly selection: SelectionSet;
  readonly snap = new SnapEngine();

  private tools = new Map<ToolId, Tool>();
  private active!: Tool;
  private lastDrawTool: ToolId = "line";
  private listeners = new Set<() => void>();

  private hint = "";
  private measureShape: MeasureShape = "none";
  private measureGhost = "";
  private lastSnap: SnapResult | null = null;
  private snapBase: Vec | null = null;
  private selfClose: Vec | null = null;
  private lastScreen: Vec = { x: 0, y: 0 };
  private lastMods = { shift: false, ctrl: false, alt: false };
  private cursorTip: string | null = null;
  private pulse: { ids: string[]; until: number } | null = null;
  private reapply: ((raw: string) => boolean) | null = null;
  private reapplyAt = 0;
  private isGestureActive = false;
  /** 3.21.0: a message for the person, on the screen (main.tsx shows it as a toast; src/tools does not import src/ui). */
  onNotice: ((text: string) => void) | null = null;
  /** 3.21.0: the door picked inside the selected wall run (a click or a drag on the door). */
  private doorPick: DoorPick | null = null;
  /** True while a drag is under way (3.20.0: the Measurements box then leaves + and - to the tool). */
  get dragging(): boolean {
    return this.isGestureActive;
  }
  private activeLayer = "";
  private clipboard: ClipboardData = { entities: [] };
  private pasteCount = 0;
  private copiedStyle: Style | null = null;
  private rightClickConsumed = false;

  /** UI hook: called with viewport-local coords and menu items on right-click. */
  onContextMenu: ((screen: Vec, items: MenuItem[]) => void) | null = null;

  /** ANN-1 inline editor state — the UI renders an overlay input when non-null. */
  textEdit: TextEditState | null = null;
  /** Counts text edits: a new edit gets a new editor box (its starting text is set once, when it opens). */
  textEditId = 0;
  private textEditCommit: ((text: string) => void) | null = null;
  private textEditCancel: (() => void) | null = null;

  prefs: Prefs = structuredClone(DEFAULT_PREFS);
  /** Set by the composition root to persist preference changes. */
  persistPrefs: ((p: Prefs) => void) | null = null;

  applyPrefs(p: Partial<Prefs>): void {
    const keymap = { ...(p.keymap ?? this.prefs.keymap) };
    // A tool added after the settings were saved gets its default key, when that key is free.
    for (const [k, tool] of Object.entries(DEFAULT_KEYMAP)) {
      if (!(k in keymap) && !Object.values(keymap).includes(tool)) keymap[k] = tool;
    }
    this.prefs = {
      ...this.prefs,
      ...p,
      keymap,
      pipe: { ...DEFAULT_PIPE_PREFS, ...(p.pipe ?? this.prefs.pipe) },
      wall: { ...DEFAULT_WALL_PREFS, ...(p.wall ?? this.prefs.wall) }
    };
    document.documentElement.dataset.theme = this.prefs.theme === "light" ? "light" : "";
    this.snap.polar45 = this.prefs.polar45;
    this.viewport.dimImages = this.prefs.dimImagesWhileDrawing && DRAW_TOOLS.has(this.active?.id ?? "select");
    this.viewport.scheduler.invalidateAll(); // theme colors + dimming live on bg
    this.notify();
  }

  setPref(patch: Partial<Prefs>): void {
    this.applyPrefs(patch);
    this.persistPrefs?.(this.prefs);
  }

  constructor(
    private viewport: Viewport,
    private store: DocStore
  ) {
    this.selection = new SelectionSet();
    const ctx = this.buildCtx();
    for (const tool of [
      new SelectTool(ctx),
      new LineTool(ctx),
      new RectTool(ctx),
      new CircleTool(ctx),
      new ArcTool(ctx),
      new CalibrateTool(ctx),
      new PerspectiveTool(ctx),
      new StraightenTool(ctx),
      new TextTool(ctx),
      new LabelTool(ctx),
      new DimensionTool(ctx),
      new GridTool(ctx),
      new FreehandTool(ctx),
      new TapeTool(ctx),
      new MarkerTool(ctx),
      new CropTool(ctx),
      new PlaceTool(ctx),
      new PipeTool(ctx),
      new WallTool(ctx)
    ]) {
      this.tools.set(tool.id, tool as Tool);
    }
    (this.tools.get("perspective") as PerspectiveTool).onPanelChange = () => this.notify();
    // The first object placed is brought into view if it does not fit on screen (an imported
    // pump is 700 mm; at the current zoom it can be bigger than the window).
    (this.tools.get("place") as PlaceTool).onFirstPlaced = (box) => this.viewport.showBounds(box);
    this.selection.subscribe(() => {
      // a picked door belongs to its wall: another selection drops it
      if (this.doorPick && !(this.selection.size === 1 && this.selection.has(this.doorPick.wallId))) this.doorPick = null;
      this.viewport.scheduler.invalidate("overlay"); // selection changes must repaint highlights
      this.notify();
    });
    store.subscribe((doc, cmd) => {
      this.selection.prune(doc);
      if (cmd) this.snap.onDocChange(cmd);
      // creation feedback: newly added entities pulse briefly on the overlay
      if (cmd?.kind === "addEntities" && cmd.entities.length > 0) {
        this.startPulse(cmd.entities.map((e) => e.id));
      }
      this.viewport.scheduler.invalidate("dynamic", "overlay");
    });

    this.wireViewport();
    this.switchTool("select");
  }

  // ---- public state -------------------------------------------------------

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  uiState(): ToolUiState {
    return {
      toolId: this.active.id,
      hint: this.hint,
      measureShape: this.measureShape,
      measureGhost: this.measureGhost,
      snapKind:
        this.lastMods.alt && this.snap.enabled
          ? "off (Alt)" // Alt suspends snapping — say so instead of silently dropping markers
          : this.lastSnap && this.lastSnap.kind !== "free"
            ? this.lastSnap.kind
            : "",
      snapEnabled: this.snap.enabled,
      orthoSticky: this.snap.orthoSticky,
      gridSnap: this.store.doc.canvasGrid.snap,
      prompt: this.prompt
    };
  }

  // ---- typed transforms and the library (owner, 2026-09-30) ------------------

  private prompt: SelectionPrompt | null = null;

  // ---- align (owner, 2026-10-02) --------------------------------------------
  /** The object the selection lines up with. Null = the selection lines up with itself. */
  alignRef: string | null = null;
  /** True while the next click on the drawing picks the reference object. */
  pickingAlignRef = false;
  /** Space left between two objects by "place beside", in millimetres. */
  alignGap = 0;
  /** 3.27.0 Space evenly: the last distance (mm) and what it measures, kept while the app is open. */
  spaceValue = 100;
  spaceMode: SpaceMode = "gap";

  startPickAlignRef(): void {
    this.pickingAlignRef = true;
    this.setHint("Align to: click the object to line up with · Esc = cancel");
    this.notify();
  }

  cancelPickAlignRef(): void {
    if (!this.pickingAlignRef) return;
    this.pickingAlignRef = false;
    this.notify();
  }

  clearAlignRef(): void {
    this.alignRef = null;
    this.pickingAlignRef = false;
    this.viewport.scheduler.invalidate("overlay");
    this.notify();
  }

  // ---- turning point (owner, 2026-10-06) ------------------------------------
  /** The object whose turning point the next click sets, or null. */
  pickingPivot: string | null = null;

  startPickPivot(id: string): void {
    this.pickingAlignRef = false;
    this.pickingPivot = id;
    this.setHint("Turning point: click the point to turn about (snaps work) · Esc = cancel");
    this.notify();
  }

  cancelPickPivot(): void {
    if (!this.pickingPivot) return;
    this.pickingPivot = null;
    this.setHint("Turning point: not changed.");
    this.notify();
  }

  /** Set (or with null, clear) the turning point of one object. */
  setPivot(id: string, p: Vec | null): boolean {
    if (isLocked(this.store.doc, id)) return false;
    const patch = setPivotPatch(this.store.doc, id, p);
    if (!patch) return false;
    this.store.dispatch({ kind: "updateEntities", patches: [patch] });
    this.viewport.scheduler.invalidate("overlay");
    this.notify();
    return true;
  }

  // ---- pipes (owner, 2026-10-06) --------------------------------------------
  /** Remember the Pipe tool's size and free-end look. */
  setPipePrefs(patch: Partial<PipePrefs>): void {
    this.setPref({ pipe: { ...this.prefs.pipe, ...patch } });
  }

  /** Remember the Wall tool's thickness, line, mode and fill (3.20.0). A new mode starts the tool again. */
  setWallPrefs(patch: Partial<WallPrefs>): void {
    const modeChanged = patch.mode !== undefined && patch.mode !== this.prefs.wall.mode;
    this.setPref({ wall: { ...this.prefs.wall, ...patch } });
    if (modeChanged) (this.tools.get("wall") as WallTool | undefined)?.modeChanged();
  }

  /**
   * A pipe `length` mm long out of end `port` of a fitting or pipe. The new pipe is selected.
   * Returns a sentence saying why nothing was drawn, or null when it worked.
   */
  extendFromPort(ownerId: string, port: number, length: number, endStyle: PipeEndStyle): string | null {
    const doc = this.store.doc;
    const fail = (why: string): string => {
      this.setHint(why);
      return why;
    };
    if (!doc.entities[ownerId]) return fail("That object is not in the drawing.");
    if (isLocked(doc, ownerId)) return fail("Locked — unlock it first.");
    if (!Number.isFinite(length) || length <= 0) return fail("Type a length more than 0.");
    const made = extendFromPortCommand(doc, this.activeLayerId, ownerId, port, length, endStyle);
    if (!made) return fail("That end already has a pipe, or it cannot take one.");
    const cmd = combine("extend pipe", [joinPatches(doc, ownerId, port), made.cmd]);
    if (cmd) this.store.dispatch(cmd);
    this.selection.set([made.groupId]);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint("Pipe drawn from the end. Change its size, length and ends in the Inspector.");
    return null;
  }

  /**
   * 3.11.0 BUILD A PIPE SYSTEM. Put the fitting of `opt` on a free pipe end and attach it, then start the Pipe tool from
   * the fitting's other end so the run goes on. Returns a sentence saying why nothing was placed, or null when it worked.
   */
  addFittingAtEnd(end: PipeEnd, opt: EndOption): string | null {
    const doc = this.store.doc;
    if (!doc.entities[end.pipeId]) return "That pipe is not in the drawing.";
    if (isLocked(doc, end.pipeId)) {
      this.setHint("Locked — unlock it first.");
      return "Locked — unlock it first.";
    }
    const made = placeFittingAtEnd(doc, this.activeLayerId, end, opt);
    if (!made) {
      this.setHint("That pipe end already has a fitting or a pipe.");
      return "That pipe end already has a fitting or a pipe.";
    }
    this.store.dispatch(made.cmd);
    this.selection.set([made.fittingId]);
    this.viewport.scheduler.invalidate("static", "overlay");
    const name = ownerName(this.store.doc, made.fittingId);
    if (made.cont !== null && this.startPipeFrom(made.fittingId, made.cont)) {
      this.setHint(`${opt.label} placed. Click or type the length of the next pipe ⏎ · right-click its end for the next fitting · Esc = end`);
    } else {
      this.setHint(`${name} placed.`);
    }
    this.notify();
    return null;
  }

  /**
   * 3.19.0 A FITTING IN THE LENGTH OF A PIPE. Put the fitting of `opt` in pipe `pipeId` at the centre-line point nearest
   * `at` and split the pipe on it (pipes/insert.ts). A side tee then starts the Pipe tool from its branch. One undo step.
   * Returns a sentence saying why nothing was put in, or null when it worked.
   */
  insertInPipe(pipeId: string, at: Vec, opt: InsertOption): string | null {
    const doc = this.store.doc;
    const fail = (why: string): string => {
      this.setHint(why);
      this.notify();
      return why;
    };
    if (!doc.entities[pipeId]) return fail("That pipe is not in the drawing.");
    if (isLocked(doc, pipeId)) return fail("Locked — unlock it first.");
    const r = insertFittingCommand(doc, pipeId, at, opt);
    if (typeof r === "string") return fail(r);
    this.store.dispatch(r.cmd);
    this.selection.set([r.fittingId]);
    this.viewport.scheduler.invalidate("static", "overlay");
    const name = ownerName(this.store.doc, r.fittingId);
    if (r.branch !== null && this.startPipeFrom(r.fittingId, r.branch)) {
      this.setHint(`${name} is in the pipe. The pipe is two pipes now. Draw the branch: click, or type its length ⏎ · Esc = end`);
    } else {
      this.setHint(`${name} is in the pipe. The pipe is two pipes now. Ctrl+Z makes it one pipe again.`);
    }
    this.notify();
    return null;
  }

  /** 3.19.0: a placed butterfly valve, lever or gearbox, open or closed, plan or side. A sentence when nothing changed. */
  editButterfly(id: string, change: Partial<ButterflySpec>): string | null {
    const doc = this.store.doc;
    if (!doc.entities[id]) return "That valve is not in the drawing.";
    if (isLocked(doc, id)) return "Locked — unlock it first.";
    const r = butterflyCommand(doc, id, change);
    if (typeof r === "string") {
      this.setHint(r);
      this.notify();
      return r;
    }
    this.store.dispatch(r);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint(`${ownerName(this.store.doc, id)}.`);
    this.notify();
    return null;
  }

  // ---- walls (3.20.0) ----------------------------------------------------------------------

  /** Run a wall change: a sentence (why not) goes to the hint and is returned; a command is dispatched. */
  private runWall(wallId: string, r: Command | { cmd: Command } | string, done: string): string | null {
    const doc = this.store.doc;
    if (!doc.entities[wallId]) return "That wall is not in the drawing.";
    if (isLocked(doc, wallId)) {
      this.setHint("Locked — unlock it first.");
      this.notify();
      return "Locked — unlock it first.";
    }
    if (typeof r === "string") {
      // 3.21.0: on the screen, not only in the status line (owner, 2026-10-21: a refused door looked like nothing)
      this.setHint(r);
      this.onNotice?.(r);
      this.notify();
      return r;
    }
    this.store.dispatch("cmd" in r ? r.cmd : r);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint(done);
    this.notify();
    return null;
  }

  /** A door of `kind` in wall `wallId`, centred at `at` along the wall, opening to the side `at` is on. The new door is picked. */
  addDoor(wallId: string, at: Vec, kind: DoorKind): string | null {
    const r = addDoorCommand(this.store.doc, wallId, at, kind);
    const moved = typeof r !== "string" && r.moved;
    const done = `${DOOR_WORDS[kind]} added${moved ? " next to the other door: that is the nearest place where it fits" : ""}. Drag it along the wall to move it. Its settings are in the Inspector.`;
    const why = this.runWall(wallId, r, done);
    if (!why && typeof r !== "string") {
      this.selection.set([wallId]);
      this.selectDoor({ wallId, doorId: r.door.id });
      if (moved) this.onNotice?.(done);
    }
    return why;
  }

  editDoor(wallId: string, doorId: string, change: Partial<Omit<WallDoor, "id" | "seg">>): string | null {
    return this.runWall(wallId, updateDoorCommand(this.store.doc, wallId, doorId, change), "Door changed.");
  }

  removeDoor(wallId: string, doorId: string): string | null {
    const why = this.runWall(wallId, removeDoorCommand(this.store.doc, wallId, doorId), "Door taken away. The wall is whole there again.");
    if (!why && this.doorPick?.doorId === doorId) this.selectDoor(null);
    return why;
  }

  /**
   * 3.21.0: the door picked inside the selected wall, or null. A pick is good only while its wall is the one selected
   * object and the door is still in it.
   */
  get selectedDoor(): DoorPick | null {
    const p = this.doorPick;
    if (!p || this.selection.size !== 1 || !this.selection.has(p.wallId)) return null;
    return wallMetaOf(this.store.doc.entities[p.wallId])?.doors.some((d) => d.id === p.doorId) ? p : null;
  }

  /** Pick door `pick` (its wall is then the selection), or drop the pick. The Inspector shows the picked door. */
  selectDoor(pick: DoorPick | null): void {
    if (pick && !(this.selection.size === 1 && this.selection.has(pick.wallId))) this.selection.set([pick.wallId]);
    const same = (this.doorPick?.wallId ?? null) === (pick?.wallId ?? null) && (this.doorPick?.doorId ?? null) === (pick?.doorId ?? null);
    this.doorPick = pick;
    if (same) return;
    this.viewport.scheduler.invalidate("dynamic", "overlay");
    this.notify();
  }

  editWall(wallId: string, change: Parameters<typeof setWallCommand>[2]): string | null {
    return this.runWall(wallId, setWallCommand(this.store.doc, wallId, change), "Wall changed.");
  }

  setWallLength(wallId: string, seg: number, length: number): string | null {
    return this.runWall(wallId, setSegmentLengthCommand(this.store.doc, wallId, seg, length), `Wall ${seg + 1} is ${Math.round(length)} mm.`);
  }

  /**
   * 3.23.0: split wall `wallId` at spine distance `gs`. An open run becomes two walls; a room becomes one open run.
   * The selection is the part or parts. Returns a sentence saying why nothing changed, or null when it worked.
   */
  splitWall(wallId: string, gs: number, done: string): string | null {
    const r = splitWallCommand(this.store.doc, wallId, gs);
    const out = this.runWall(wallId, r, done);
    if (out === null && typeof r !== "string") {
      this.selectDoor(null);
      this.selection.set(r.ids.filter((id) => this.store.doc.entities[id]));
      this.notify();
    }
    return out;
  }

  /** The right-click menu on a wall: add a door there, or (on a door) change it or take it away. */
  private wallMenu(id: string, at: Vec): MenuItem[] {
    const doc = this.store.doc;
    const w = wallMetaOf(doc.entities[id]);
    if (!w) return [];
    const locked = isLocked(doc, id);
    const door = doorAt(doc, id, at);
    if (door) {
      this.selectDoor({ wallId: id, doorId: door.id }); // the Inspector shows the door the menu is for
      const kinds: DoorKind[] = ["single", "double", "roller"];
      return [
        { label: `${DOOR_WORDS[door.kind]} · ${Math.round(door.width)} mm`, disabled: true },
        { label: door.kind === "roller" ? "Drum to the other side" : "Open to the other side", action: () => void this.editDoor(id, door.id, { side: door.side === 1 ? -1 : 1 }), disabled: locked },
        ...(door.kind === "single" ? [{ label: "Hinges on the other jamb", action: () => void this.editDoor(id, door.id, { hinge: door.hinge === "start" ? "end" : "start" }), disabled: locked } as MenuItem] : []),
        { label: "Change to", children: kinds.filter((k) => k !== door.kind).map((k) => ({ label: DOOR_WORDS[k], action: () => void this.editDoor(id, door.id, { kind: k }) })), disabled: locked },
        { label: "Remove this door", action: () => void this.removeDoor(id, door.id), disabled: locked }
      ];
    }
    // 3.23.0: split the wall here, break it at the corner near the pointer, or split it where it meets another wall
    const near = Math.max(w.thickness, 12 / this.viewport.camera.current.scale);
    const corner = wallCornerAt(doc, id, at, near);
    const here = wallSegmentAt(doc, id, at);
    const meet = wallMeetings(doc, id)
      .map((m) => ({ ...m, d: Math.hypot(m.p.x - at.x, m.p.y - at.y) }))
      .sort((a, b) => a.d - b.d)[0];
    const parts: MenuItem[] = [];
    if (corner) parts.push({ label: w.closed ? "Break the room at this corner" : "Break the wall at this corner", action: () => void this.splitWall(id, corner.g, "Wall broken at the corner. Drag an end to move it."), disabled: locked, separator: true });
    else if (here) parts.push({ label: "Split the wall here", action: () => void this.splitWall(id, here.seg.start + here.u, w.closed ? "Room opened here. It is one wall with two ends." : "Wall split in two."), disabled: locked, separator: true });
    if (meet) parts.push({ label: "Split where it meets the other wall", action: () => void this.splitWall(id, meet.g, "Wall split where it meets the other wall."), disabled: locked, separator: !parts.length });
    return [
      { label: `Wall · ${Math.round(w.thickness)} mm · add a door here`, disabled: true },
      ...(["single", "double", "roller"] as const).map((k) => ({ label: `${DOOR_WORDS[k]} · ${DOOR_WIDTH[k]} mm`, action: () => void this.addDoor(id, at, k), disabled: locked })),
      ...parts
    ];
  }

  /** The right-click menu on the length of pipe `pipeId`: the fittings that the pipe passes through, put in at `at`. */
  private insertMenu(pipeId: string, at: Vec): MenuItem[] {
    const doc = this.store.doc;
    const ax = pipeAxis(doc, pipeId);
    if (!ax) return [];
    const locked = isLocked(doc, pipeId);
    const opts = insertOptions({ size: ax.size, u: ax.u });
    const item = (o: InsertOption, separator = false, label = o.label): MenuItem => ({ label, action: () => void this.insertInPipe(pipeId, at, o), disabled: locked, separator });
    const items: MenuItem[] = [{ label: `Pipe · ${ax.size} mm · put a fitting in here`, disabled: true }];
    opts.filter((o) => o.group === "tee").forEach((o, i) => items.push(item(o, i === 0)));
    const smaller = opts.filter((o) => o.group === "tee-smaller");
    if (smaller.length) items.push({ label: "Tee with a smaller branch", children: smaller.map((o) => item(o)), disabled: locked });
    const bf = opts.filter((o) => o.group === "butterfly");
    const prefix = `Butterfly valve · DN${ax.size} · `;
    const word = (o: InsertOption) => {
      const rest = o.label.startsWith(prefix) ? o.label.slice(prefix.length) : o.label;
      return rest.charAt(0).toUpperCase() + rest.slice(1);
    };
    if (bf.length) bf.forEach((o, i) => items.push(item(o, i === 0, `Butterfly valve · ${word(o)}`)));
    else items.push({ label: `Butterfly valve · none of ${ax.size} mm (ERA lever ${operatorSizes("lever")}, gearbox ${operatorSizes("gearbox")})`, disabled: true, separator: true });
    opts.filter((o) => o.group === "checkvalve").forEach((o, i) => items.push(item(o, i === 0)));
    return items;
  }

  /**
   * 3.12.0: give a placed bend, tee or reducer another size. `size2` is the tee branch or the reducer's small end. With
   * `withPipes` the pipes on its ends (and the pipes joined to them) take the size of their end. One undo step.
   * Returns a sentence saying why nothing changed, or null when it worked.
   */
  resizeFitting(id: string, size: number, size2?: number, withPipes = true): string | null {
    const doc = this.store.doc;
    if (!doc.entities[id]) return "That fitting is not in the drawing.";
    if (isLocked(doc, id)) return "Locked — unlock it first.";
    const r = resizeFittingCommand(doc, id, size, size2, withPipes);
    if (typeof r === "string") {
      this.setHint(r);
      this.notify();
      return r;
    }
    this.store.dispatch(r.cmd);
    this.viewport.scheduler.invalidate("static", "overlay");
    const name = ownerName(this.store.doc, id);
    this.setHint(r.pipesChanged ? `${name}. ${r.pipesChanged === 1 ? "1 pipe changed to the size of its end" : `${r.pipesChanged} pipes changed to the size of their ends`}.` : `${name}.`);
    this.notify();
    return null;
  }

  /**
   * Change a placed lint pot: chamber diameter, ends (inlet or outlet, size, angle as drawn, length), an end added or
   * taken away. One undo step. Returns a sentence saying why nothing changed, or null when it worked.
   */
  editLintPot(id: string, edit: LintPotEdit): string | null {
    const doc = this.store.doc;
    if (!doc.entities[id]) return "That lint pot is not in the drawing.";
    if (isLocked(doc, id)) return "Locked — unlock it first.";
    const r = lintPotCommand(doc, id, edit);
    if (typeof r === "string") {
      this.setHint(r);
      this.notify();
      return r;
    }
    this.store.dispatch(r.cmd);
    this.viewport.scheduler.invalidate("static", "overlay");
    const words: string[] = [ownerName(this.store.doc, id)];
    if (r.pipesChanged) words.push(r.pipesChanged === 1 ? "1 pipe changed to the size of its end" : `${r.pipesChanged} pipes changed to the size of their ends`);
    if (r.pipesFreed) words.push(r.pipesFreed === 1 ? "1 pipe is free now: its end was taken away" : `${r.pipesFreed} pipes are free now: their end was taken away`);
    this.setHint(`${words.join(". ")}.`);
    this.notify();
    return null;
  }

  /**
   * 3.16.0 (the start of the pump and motor elevations, owner 2026-10-20): place the SIDE VIEW (elevation) of pump plan
   * `id` to the right of it, at the sizes the plan is drawn at. A sentence when it cannot be placed.
   */
  placePumpElevation(id: string): string | null {
    const doc = this.store.doc;
    const g = doc.entities[id];
    const info = g?.type === "group" ? (g.meta?.pump as PumpInfo | undefined) : undefined;
    if (!g || !info) return "Select a pump.";
    if (info.view !== "plan") return "This pump is a side view (an elevation) already.";
    const item = pumpItemFor(info, "side");
    if (!item) return "This pump is not in the TraceCAD tables.";
    const box = objectBox(doc, id);
    if (!box) return "This pump has nothing to measure.";
    const at = { x: box.maxX + 300 + item.size.w / 2, y: (box.minY + box.maxY) / 2 };
    const result = pasteCommand({ entities: item.entities }, this.activeLayerId, at);
    if (!result) return "The side view could not be placed.";
    this.store.dispatch(result.cmd);
    const sideId = result.newIds.find((x) => this.store.doc.entities[x]?.meta?.pump);
    // the side view takes the sizes the plan is drawn at
    const { dnIn, dnOut } = drawnSizes(info);
    if (sideId && (dnIn !== info.inletDn || dnOut !== info.outletDn)) {
      const r = redrawPumpCommand(this.store.doc, sideId, dnIn, dnOut);
      if (typeof r !== "string") this.store.dispatch({ kind: "batch", label: "side view sizes", commands: [r.cmd] });
    }
    if (sideId) this.selection.set([sideId]);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint("Side view (elevation) placed to the right of the plan. It is a pump of its own: its sizes change in the Inspector.");
    this.notify();
    return null;
  }

  /**
   * 3.16.0: place the ELEVATION of lint pot `id` beside it: the same values, seen from the front (from the bottom of the
   * plan). Its ends follow the angles AS DRAWN. It is a lint pot of its own: change it in the Inspector. A sentence when
   * it cannot be placed.
   */
  placeLintPotElevation(id: string): string | null {
    const doc = this.store.doc;
    const placed = placedLintPot(doc, id);
    if (typeof placed === "string") return placed;
    if (placed.spec.view === "elevation") return "This lint pot is an elevation already.";
    const spec: LintPotSpec = { ...placed.spec, view: "elevation" };
    let item;
    try {
      item = makeLintPotItem(spec, "elevation", lintPotName(spec));
    } catch (e) {
      return (e as Error).message.replace(/^lint pot elevation: /, "");
    }
    // to the right of the plan, clear of its ends
    const reach = placed.spec.diameter / 2 + Math.max(...placed.spec.ends.map((e) => e.length)) + placed.spec.diameter;
    const at = { x: placed.centre.x + reach + item.size.w / 2, y: placed.centre.y };
    const result = pasteCommand({ entities: item.entities }, this.activeLayerId, at);
    if (!result) return "The elevation could not be placed.";
    this.store.dispatch(result.cmd);
    const groupId = result.newIds.find((x) => this.store.doc.entities[x]?.meta?.lintpot);
    if (groupId) this.selection.set([groupId]);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint("Elevation placed to the right of the plan. It is seen from the bottom of the plan. Change it in the Inspector.");
    this.notify();
    return null;
  }

  /**
   * 3.15.3: the pipe size on end `index` of a component (a pump's inlet or outlet). The drawing does not change. With
   * `withPipes` the pipes on that end, and the pipes joined to them, take the size. A sentence when nothing changed.
   */
  setPortSize(id: string, index: number, size: number, withPipes = true): string | null {
    const doc = this.store.doc;
    if (!doc.entities[id]) return "That object is not in the drawing.";
    if (isLocked(doc, id)) return "Locked — unlock it first.";
    const r = setPortSizeCommand(doc, id, index, size, withPipes);
    if (typeof r === "string") {
      this.setHint(r);
      this.notify();
      return r;
    }
    this.store.dispatch(r.cmd);
    this.viewport.scheduler.invalidate("static", "overlay");
    const label = portsOf(this.store.doc, id)[index]?.label ?? `end ${index + 1}`;
    const pipes = r.pipesChanged ? ` ${r.pipesChanged === 1 ? "1 pipe changed" : `${r.pipesChanged} pipes changed`} to ${size} mm.` : "";
    this.setHint(`${ownerName(this.store.doc, id)}: the ${label} pipe size is ${size} mm.${pipes}${r.note ? ` ${r.note}` : ""}`);
    this.notify();
    return null;
  }

  /** The Pipe tool, drawing from a free end of a fitting, pump or pipe. False when that end is not free. */
  startPipeFrom(ownerId: string, index: number): boolean {
    this.switchTool("pipe");
    const tool = this.tools.get("pipe");
    const ok = tool instanceof PipeTool && tool.beginAt(ownerId, index);
    this.viewport.scheduler.invalidate("dynamic", "overlay");
    this.notify();
    return ok;
  }

  /** The free end of a fitting, pump or pipe under a screen point (a little slack), or null. */
  private freeEndAt(screen: Vec, slackPx = 12): Port | null {
    const world = this.viewport.cursorWorld(screen);
    return freePortNear(this.store.doc, world, slackPx / this.viewport.camera.current.scale);
  }

  /** The right-click menu of a free end: fittings for a pipe end, "Draw a pipe from here" for a fitting or pump end. */
  private endMenu(port: Port): MenuItem[] {
    const doc = this.store.doc;
    const locked = isLocked(doc, port.ownerId);
    const draw: MenuItem = { label: "Draw a pipe from here", action: () => void this.startPipeFrom(port.ownerId, port.index), disabled: locked };
    if (port.ownerKind !== "pipe") {
      return [{ label: `${ownerName(doc, port.ownerId)} · ${port.label} · ${port.size} mm`, disabled: true }, draw];
    }
    const end: PipeEnd = { pipeId: port.ownerId, index: port.index === 0 ? 0 : 1, p: port.p, dir: port.dir, size: port.size };
    const opts = endOptions(end);
    const item = (o: EndOption, separator = false): MenuItem => ({ label: o.label, action: () => void this.addFittingAtEnd(end, o), disabled: locked, separator });
    const bends = opts.filter((o) => o.group === "bend");
    const tees = opts.filter((o) => o.group === "tee");
    const smaller = opts.filter((o) => o.group === "tee-smaller");
    const reducers = opts.filter((o) => o.group === "reducer");
    const items: MenuItem[] = [{ label: `Pipe end · ${port.size} mm · add a fitting`, disabled: true }];
    bends.forEach((o, i) => items.push(item(o, i === 0)));
    tees.forEach((o, i) => items.push(item(o, i === 0)));
    if (smaller.length) items.push({ label: "Tee with a smaller branch", children: smaller.map((o) => item(o)), disabled: locked });
    if (reducers.length) items.push({ label: "Reducer", children: reducers.map((o) => item(o)), disabled: locked });
    opts.filter((o) => o.group === "valve").forEach((o, i) => items.push(item(o, i === 0)));
    // 3.25.0: butterfly valves (lever, gearbox; open, closed) in their own submenu
    const butterflies = opts.filter((o) => o.group === "butterfly");
    if (butterflies.length) {
      const first = !opts.some((o) => o.group === "valve");
      items.push({ label: `Butterfly valve · DN${port.size}`, children: butterflies.map((o) => item(o)), disabled: locked, separator: first });
    }
    opts.filter((o) => o.group === "cap").forEach((o, i) => items.push(item(o, i === 0))); // 3.22.0
    const style = (() => {
      const pm = pipeMetaOf(doc.entities[port.ownerId]);
      return pm ? (port.index === 0 ? pm.endA : pm.endB) : null;
    })();
    const setEnd = (v: PipeEndStyle) => () => void this.editPipe(port.ownerId, port.index === 0 ? { endA: v } : { endB: v });
    items.push(
      { label: "End: flat", action: setEnd("flat"), disabled: locked || style === "flat", separator: true },
      { label: "End: floor penetration (circle)", action: setEnd("circle"), disabled: locked || style === "circle" },
      { label: "End: rubber joint (flexible coupler)", action: setEnd("flex"), disabled: locked || style === "flex" },
      { ...draw, separator: true }
    );
    return items;
  }

  /** Change a pipe (size, length, end styles, anchors) as one undo step. */
  editPipe(id: string, change: Partial<PipeMeta> & { length?: number }): boolean {
    const doc = this.store.doc;
    if (!doc.entities[id] || isLocked(doc, id)) return false;
    const patches = setPipePatches(doc, id, change);
    if (patches.length === 0) return false;
    this.store.dispatch({ kind: "updateEntities", patches });
    this.viewport.scheduler.invalidate("static", "overlay");
    this.notify();
    return true;
  }

  /** Free one end of a pipe where it is now. The end gets a flat end. */
  detachPipeEnd(id: string, end: "a" | "b"): boolean {
    const doc = this.store.doc;
    const meta = pipeMetaOf(doc.entities[id]);
    const ends = pipeEnds(doc, id);
    if (!meta || !ends || isLocked(doc, id)) return false;
    const anchor = end === "a" ? meta.a : meta.b;
    if (anchor.kind !== "port") return false;
    const p = end === "a" ? ends.a : ends.b;
    const cmd = combine("detach pipe end", [
      unjoinPatches(doc, anchor.ownerId, anchor.port),
      setPipePatches(doc, id, end === "a" ? { a: { kind: "free", p }, endA: "flat" } : { b: { kind: "free", p }, endB: "flat" })
    ]);
    if (!cmd) return false;
    this.store.dispatch(cmd);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint(`End ${end.toUpperCase()} detached. It stays where it is. Drag its grip to move it.`);
    return true;
  }

  /** The end of a pipe that a new pipe can go on from: 1 (end B) when free, else 0 (end A), else null. */
  pipeContinuePort(id: string): number | null {
    const doc = this.store.doc;
    const meta = pipeMetaOf(doc.entities[id]);
    if (!meta) return null;
    const ports = portsOf(doc, id);
    const free = (i: number): boolean => (i === 0 ? meta.a : meta.b).kind === "free" && !ports[i]?.taken;
    return free(1) ? 1 : free(0) ? 0 : null;
  }

  /** A new pipe `length` mm long on from the free end of a pipe. */
  continuePipe(id: string, length: number): string | null {
    const port = this.pipeContinuePort(id);
    if (port === null) {
      const why = "Both ends of this pipe are attached. Detach one end first.";
      this.setHint(why);
      return why;
    }
    return this.extendFromPort(id, port, length, this.prefs.pipe.end);
  }

  /** The box of the reference object, or null when none is set or it was deleted. */
  alignRefBox(): ReturnType<typeof objectBox> {
    if (!this.alignRef || !this.store.doc.entities[this.alignRef]) return null;
    return objectBox(this.store.doc, this.alignRef);
  }

  /**
   * Align the selection. Returns a sentence saying why nothing moved, or null when it worked.
   * Without a reference object the selection lines up with its own box (needs two or more
   * objects). With one, each selected object lines up with that object, which itself stays put.
   */
  alignSelection(
    op:
      | { kind: "edge"; edge: AlignEdge }
      | { kind: "centre" }
      | { kind: "beside"; side: Side }
      | { kind: "distribute"; axis: "h" | "v" }
      | { kind: "space"; axis: "h" | "v"; mode: SpaceMode; value: number }
  ): string | null {
    const doc = this.store.doc;
    if (this.alignRef && !doc.entities[this.alignRef]) this.alignRef = null;
    const ids = movableIds(doc, this.selection.all, this.alignRef);
    const fail = (why: string): string => {
      this.setHint(why);
      return why;
    };
    if (ids.length === 0) return fail("Nothing to move: select an object first. Locked objects do not move.");
    const ref = this.alignRefBox();
    let patches: ReturnType<typeof alignPatches> = [];
    if (op.kind === "space") {
      // 3.27.0: space evenly by a set gap or centre distance; the first object stays. One undo step.
      const made = spacePatches(doc, ids, op.axis, op.mode, op.value);
      if (typeof made === "string") return fail(made);
      if (made.length === 0) {
        this.setHint("Already spaced so.");
        return null;
      }
      this.store.dispatch({ kind: "batch", label: "space evenly", commands: [{ kind: "updateEntities", patches: made }] });
      this.viewport.scheduler.invalidate("static", "overlay");
      const n = spaceUnits(this.store.doc, ids, op.axis).length;
      this.setHint(`Spaced ${n} objects ${op.axis === "v" ? "top to bottom" : "left to right"}: ${op.value} mm ${op.mode === "gap" ? "between edges" : "centre to centre"}. The first one stayed.`);
      return null;
    }
    if (op.kind === "distribute") {
      if (ids.length < 3) return fail("Spread out needs three or more selected objects.");
      patches = distributePatches(doc, ids, op.axis);
    } else if (op.kind === "beside") {
      if (!ref) return fail("Pick the object to place it beside first (Pick object).");
      patches = besidePatches(doc, ids, op.side, ref, this.alignGap);
    } else {
      if (!ref && ids.length < 2) return fail("Pick the object to align to, or select two or more objects.");
      patches = op.kind === "edge" ? alignPatches(doc, ids, op.edge, ref) : centrePatches(doc, ids, ref);
    }
    if (patches.length === 0) {
      this.setHint("Already lined up.");
      return null;
    }
    this.store.dispatch({ kind: "updateEntities", patches });
    this.viewport.scheduler.invalidate("static", "overlay");
    this.setHint(ref ? "Aligned to the reference object." : "Aligned.");
    return null;
  }

  /** Open a typed-number box for the selection. Nothing selected = a hint, no box. */
  openPrompt(kind: SelectionPrompt): void {
    if (this.selection.size === 0) {
      this.setHint(kind === "save" ? "Save to library: select the objects first (B)" : `${kind === "move" ? "Move by" : "Rotate by"}: select something first`);
      return;
    }
    this.prompt = kind;
    this.notify();
  }

  closePrompt(): void {
    if (this.prompt === null) return;
    this.prompt = null;
    this.notify();
  }

  /** Move the selection by dx, dy (millimetres). copy = move a copy and keep the original. */
  moveSelectionBy(dx: number, dy: number, copy: boolean): boolean {
    const ids = this.unlockedSelection();
    if (ids.length === 0 || !Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) return false;
    if (copy) {
      const result = pasteForMove(copyForMove(this.store.doc, ids), null, { x: dx, y: dy });
      if (!result) return false;
      this.store.dispatch(result.cmd);
      const top = new Set(result.newIds);
      for (const id of result.newIds) {
        const e = this.store.doc.entities[id];
        if (e?.type === "group") for (const c of e.children) top.delete(c);
      }
      this.selection.set([...top]);
    } else {
      this.store.dispatch(moveCommand(this.store.doc, ids, { x: dx, y: dy }));
    }
    this.viewport.scheduler.invalidate("static", "overlay");
    return true;
  }

  /**
   * Rotate the selection. One object with a set turning point turns about that point; anything
   * else turns about the centre of the selection. Positive degrees turn clockwise on screen.
   */
  rotateSelectionBy(deg: number): boolean {
    const ids = this.unlockedSelection();
    if (ids.length === 0 || !Number.isFinite(deg) || deg === 0) return false;
    const about = turnPoint(this.store.doc, ids);
    if (!about) return false;
    const patches = rotatePatches(this.store.doc, ids, about, deg);
    if (!patches.length) return false;
    this.store.dispatch({ kind: "updateEntities", patches });
    this.viewport.scheduler.invalidate("static", "overlay");
    return true;
  }

  /** Pick up a library object and place it with clicks. */
  startPlace(item: LibraryItem): void {
    (this.tools.get("place") as PlaceTool).setItem(item);
    if (this.active.id === "place") this.active.activate();
    else this.switchTool("place");
    this.notify();
  }

  switchTool(id: ToolId): void {
    const next = this.tools.get(id);
    if (!next) return;
    if (this.active) {
      if (this.active === next) return;
      this.active.cancel();
      this.active.deactivate();
      if (this.active.id !== "select") this.lastDrawTool = this.active.id;
    }
    this.active = next;
    this.snap.clearCharged();
    this.cursorTip = null;
    this.reapply = null; // TL-4 re-edit window never outlives its tool
    this.viewport.setCursor(TOOL_CURSOR[next.id] ?? null);
    // IMG-12 "dim images while drawing" tracks the active tool kind
    this.viewport.dimImages = this.prefs.dimImagesWhileDrawing && DRAW_TOOLS.has(next.id);
    this.viewport.scheduler.invalidate("bg");
    next.activate();
    this.notify();
  }

  get activeToolId(): ToolId {
    return this.active.id;
  }

  repeatLastTool(): void {
    this.switchTool(this.lastDrawTool); // Space tap (TL-2)
  }

  toggleSnap(): void {
    this.snap.enabled = !this.snap.enabled;
    this.notify();
  }

  toggleOrtho(): void {
    this.snap.orthoSticky = !this.snap.orthoSticky;
    this.notify();
  }

  toggleGridSnap(): void {
    const doc = this.store.doc;
    this.store.dispatch({
      kind: "setDocProps",
      before: { canvasGrid: { ...doc.canvasGrid } },
      after: { canvasGrid: { ...doc.canvasGrid, snap: !doc.canvasGrid.snap } }
    });
    this.notify();
  }

  // ---- edit actions (context menu + shortcuts; also used by dock panels) -------

  private dispatchAndRender(cmd: Command | null): void {
    if (!cmd) return;
    this.store.dispatch(cmd);
    this.viewport.scheduler.invalidate("static", "overlay");
  }

  private unlockedSelection(): string[] {
    return [...this.selection.all].filter((id) => !isLocked(this.store.doc, id));
  }

  copySelection(): void {
    if (this.selection.size === 0) return;
    this.clipboard = copyEntities(this.store.doc, this.selection.all);
    this.pasteCount = 0;
  }

  cutSelection(): void {
    if (this.selection.size === 0) return;
    this.copySelection();
    this.deleteSelection();
  }

  paste(): void {
    this.pasteCount++;
    const offset = { x: 5 * this.pasteCount, y: 5 * this.pasteCount }; // TL-7 cascade
    const layerId = this.activeLayerId;
    const result = pasteCommand(this.clipboard, layerId, offset);
    if (!result) return; // EC-TL-11
    this.store.dispatch(result.cmd);
    // select the top-level objects (a group's parts follow the group)
    const doc = this.store.doc;
    const top = new Set(result.newIds);
    for (const id of result.newIds) {
      const e = doc.entities[id];
      if (e?.type === "group") for (const c of e.children) top.delete(c);
    }
    this.selection.set([...top]);
    const layer = doc.layers.find((l) => l.id === layerId);
    this.setHint(`Pasted ${top.size} ${top.size === 1 ? "object" : "objects"} on the layer "${layer?.name ?? "?"}" (the active layer).`);
  }

  duplicateSelection(): void {
    const result = pasteCommand(copyEntities(this.store.doc, this.selection.all), null, { x: 5, y: 5 });
    if (!result) return;
    this.store.dispatch(result.cmd);
    this.selection.set(result.newIds);
  }

  deleteSelection(): void {
    const ids = this.unlockedSelection();
    if (ids.length === 0) return;
    this.dispatchAndRender(makeDeleteCommand(this.store.doc, ids));
  }

  groupSelection(): void {
    // Only objects on a layer can be grouped. A part of a group (selected while editing the group, or still selected
    // after an undo) would end up in two groups: deleting one would delete parts of the other (2026-10-20).
    const doc = this.store.doc;
    const inGroup = new Set<string>();
    for (const e of Object.values(doc.entities)) if (e.type === "group") e.children.forEach((c) => inGroup.add(c));
    const all = this.unlockedSelection();
    const ids = all.filter((id) => !inGroup.has(id) && !!doc.entities[id]);
    if (ids.length < all.length && ids.length < 2) {
      this.setHint("Group: these are parts of a group already. Ungroup that group first, or select whole objects.");
      return;
    }
    if (ids.length < 2) {
      this.setHint("Group: select two or more objects first (Ctrl+G)");
      return;
    }
    const cmd = makeGroupCommand(this.store.doc, ids, this.activeLayerId);
    this.store.dispatch(cmd);
    if (cmd.kind === "group") this.selection.set([cmd.group.id]);
    this.setHint(`Grouped ${ids.length} objects — they move as one now · Ctrl+Shift+G = ungroup`);
  }

  ungroupSelection(): void {
    // 3.21.0: a wall run is not ungrouped. Its lines are drawn from the run and its doors; loose, they are not a wall.
    const groups = [...this.selection.all].filter((id) => this.store.doc.entities[id]?.type === "group" && !wallMetaOf(this.store.doc.entities[id]));
    if (groups.length === 0) {
      this.setHint([...this.selection.all].some((id) => wallMetaOf(this.store.doc.entities[id])) ? "A wall is not ungrouped. Change it in the Inspector, or right-click it to add a door." : "Ungroup: select a group first (Ctrl+Shift+G)");
      return;
    }
    const released: string[] = [];
    const commands = groups.map((id) => {
      const cmd = makeUngroupCommand(this.store.doc, id);
      if (cmd.kind === "ungroup") released.push(...cmd.children.map((c) => c.id));
      return cmd;
    });
    this.store.dispatch({ kind: "batch", label: "ungroup", commands });
    this.selection.set(released);
    this.setHint(`Ungrouped — ${released.length} objects released`);
  }

  reorderSelection(action: "front" | "forward" | "backward" | "back"): void {
    this.dispatchAndRender(zOrderCommand(this.store.doc, this.unlockedSelection(), action));
  }

  /**
   * Draw a line, rectangle or circle from typed numbers (the tool palette's number
   * boxes). One undoable command; the new shape is selected and brought into view.
   * Returns the reason when the numbers draw nothing.
   */
  drawByNumbers(spec: NumberShape): string | null {
    const problem = numberShapeProblem(spec);
    if (problem) return problem;
    const doc = this.store.doc;
    const base = { id: newId(), layerId: this.activeLayerId, locked: false, hidden: false, style: structuredClone(doc.defaultStyle) };
    const g = numberShapeGeometry(spec);
    const entity: PolylineEntity | CircleEntity =
      "center" in g
        ? { ...base, type: "circle", center: g.center, r: g.r }
        : { ...base, type: "polyline", points: g.points, closed: g.closed };
    this.store.dispatch({ kind: "addEntities", entities: [entity], z: [{ layerId: entity.layerId, index: -1 }] });
    this.selection.set([entity.id]);
    this.viewport.showBounds(entityBounds(entity, this.store.doc));
    this.viewport.scheduler.invalidate("overlay");
    this.notify();
    return null;
  }

  moveSelectionToLayer(layerId: string): void {
    this.dispatchAndRender(moveToLayerCommand(this.store.doc, this.unlockedSelection(), layerId));
  }

  lockSelection(locked: boolean): void {
    this.dispatchAndRender(setLockedCommand(this.store.doc, [...this.selection.all], locked));
  }

  // ---- image actions (IMG-6/7/9/12) --------------------------------------------

  setHint(text: string): void {
    this.hint = text;
    this.notify();
  }

  getPerspective(): PerspectiveTool {
    return this.tools.get("perspective") as PerspectiveTool;
  }

  startPerspective(entityId: string): void {
    (this.tools.get("perspective") as PerspectiveTool).setTarget(entityId);
    this.switchTool("perspective");
  }

  startStraighten(entityId: string): void {
    (this.tools.get("straighten") as StraightenTool).setTarget(entityId);
    this.switchTool("straighten");
  }

  markerKind: MarkerKind = "cloud"; // remembered for the toolbar active state

  /** TL-6 Marker flyout: pick cloud or highlighter, then activate. */
  startMarker(kind: MarkerKind): void {
    this.markerKind = kind;
    (this.tools.get("marker") as MarkerTool).setKind(kind);
    this.switchTool("marker");
    this.notify();
  }

  /** IMG-6: enter crop mode on an image. */
  startCrop(entityId: string): void {
    (this.tools.get("crop") as CropTool).setTarget(entityId);
    this.switchTool("crop");
  }

  /** IMG-6: clear an image's crop (Inspector / context menu). */
  resetCrop(entityId: string): void {
    const e = this.store.doc.entities[entityId];
    if (!e || e.type !== "image" || !e.crop) return;
    this.store.dispatch({
      kind: "updateEntities",
      patches: [{ id: entityId, before: { crop: e.crop }, after: { crop: undefined } }]
    });
  }

  removeCorrection(entityId: string): void {
    (this.tools.get("perspective") as PerspectiveTool).setTarget(entityId);
    (this.tools.get("perspective") as PerspectiveTool).removeCorrection();
  }

  /** IMG-6 "Lock as underlay": bottom layer + locked + 50% opacity, one undo step. */
  lockAsUnderlay(entityId: string): void {
    const doc = this.store.doc;
    const entity = doc.entities[entityId];
    if (!entity || entity.type !== "image") return;
    const background = doc.layers[0]!;
    const commands: Command[] = [];
    if (entity.layerId !== background.id) {
      const list = doc.zOrder[entity.layerId] ?? [];
      commands.push({
        kind: "reorder",
        moves: [
          {
            id: entityId,
            from: { layerId: entity.layerId, index: list.indexOf(entityId) },
            to: { layerId: background.id, index: -1 }
          }
        ]
      });
    }
    commands.push({
      kind: "updateEntities",
      patches: [
        {
          id: entityId,
          before: { locked: entity.locked, opacity: entity.opacity },
          after: { locked: true, opacity: 0.5 }
        }
      ]
    });
    this.store.dispatch({ kind: "batch", label: "lock as underlay", commands });
    this.selection.clear();
    this.viewport.scheduler.invalidate("bg", "overlay");
    this.setHint("Locked as underlay — trace over it; [ and ] adjust its opacity");
  }

  /** IMG-12: [ / ] adjust the topmost locked image under the cursor, else the selected image. */
  private adjustImageOpacity(delta: number): boolean {
    const doc = this.store.doc;
    const world = this.viewport.cursorWorld(this.lastScreen);
    let target: string | null = null;
    for (let li = doc.layers.length - 1; li >= 0 && !target; li--) {
      const order = doc.zOrder[doc.layers[li]!.id] ?? [];
      for (let zi = order.length - 1; zi >= 0; zi--) {
        const e = doc.entities[order[zi]!];
        if (e?.type === "image" && e.locked && !e.hidden && pointInImage(e, world)) {
          target = e.id;
          break;
        }
      }
    }
    if (!target) {
      const sel = [...this.selection.all];
      if (sel.length === 1 && doc.entities[sel[0]!]?.type === "image") target = sel[0]!;
    }
    if (!target) {
      this.setHint("Select or lock an image first ( [ and ] adjust image opacity )"); // EC-IMG-16
      return true;
    }
    const e = doc.entities[target]!;
    if (e.type !== "image") return true;
    const next = Math.max(0, Math.min(1, e.opacity + delta));
    if (Math.abs(next - e.opacity) > 1e-9) {
      this.store.dispatch({
        kind: "updateEntities",
        patches: [{ id: target, before: { opacity: e.opacity }, after: { opacity: next } }]
      });
      this.viewport.scheduler.invalidate("bg");
      this.setHint(`Image opacity ${(next * 100).toFixed(0)}%`);
    }
    return true;
  }

  copyStyle(): void {
    const id = [...this.selection.all][0];
    const entity = id ? this.store.doc.entities[id] : undefined;
    if (entity && "style" in entity) {
      this.copiedStyle = structuredClone(entity.style);
      this.notify();
    }
  }

  pasteStyle(): void {
    if (!this.copiedStyle) return;
    this.dispatchAndRender(applyStyleCommand(this.store.doc, this.unlockedSelection(), this.copiedStyle));
  }

  // ---- context menu (UI-4 + user-requested point surgery) ----------------------

  private buildContextMenu(screen: Vec): MenuItem[] | null {
    if (this.rightClickConsumed) {
      this.rightClickConsumed = false;
      return null; // right-click finished a multi-point input (TL-2)
    }
    if (this.active.inProgress()) return null;

    // 3.11.0: a right-click on a free end of a pipe, fitting or pump opens the menu of that end
    const freeEnd = this.freeEndAt(screen, this.active.id === "pipe" ? 24 : 12);
    if (freeEnd) return this.endMenu(freeEnd);

    const doc = this.store.doc;
    const world = this.viewport.cursorWorld(screen);
    const scale = this.viewport.camera.current.scale;
    const hit = hitTest({ doc, index: this.viewport.index }, world, HIT_TOL_PX / scale);
    if (hit && !this.selection.has(hit)) this.selection.set([hit]);
    if (!hit && this.selection.size === 0) {
      return [
        { label: "Paste", action: () => this.paste(), disabled: this.clipboard.entities.length === 0 },
        { label: "Select all", action: () => this.selectAll() },
        { label: "Clear guides", action: () => this.clearGuides(), disabled: this.viewport.guides.length === 0 },
        { label: "Zoom to fit", action: () => this.viewport.zoomExtents() }
      ];
    }

    const sel = [...this.selection.all];
    const items: MenuItem[] = [];

    // 3.19.0: a right-click on the length of a pipe puts a fitting in the pipe there (tees, butterfly and check valves)
    if (sel.length === 1 && hit === sel[0] && looksLikePipe(doc.entities[hit])) items.push(...this.insertMenu(hit, world));
    // 3.20.0: a right-click on a wall adds a door there; on a door it changes the door or takes it away
    // (the swing of a door is empty space: a right-click there, with the wall selected, is on the door)
    if (sel.length === 1 && wallMetaOf(doc.entities[sel[0]!]) && (hit === sel[0] || doorAt(doc, sel[0]!, world))) items.push(...this.wallMenu(sel[0]!, world));

    // image verbs — exactly one image selected (IMG-6/7/9)
    if (sel.length === 1 && doc.entities[sel[0]!]?.type === "image") {
      const id = sel[0]!;
      const image = doc.entities[id]!;
      const rectified = image.type === "image" && !!image.homography;
      items.push(
        { label: "Adjust perspective", action: () => this.startPerspective(id), disabled: isLocked(doc, id) },
        {
          label: "Remove perspective correction",
          action: () => this.removeCorrection(id),
          disabled: !rectified || isLocked(doc, id)
        },
        { label: "Straighten…", action: () => this.startStraighten(id), disabled: isLocked(doc, id) },
        { label: "Crop", action: () => this.startCrop(id), disabled: isLocked(doc, id) },
        {
          label: "Reset crop",
          action: () => this.resetCrop(id),
          disabled: !(image.type === "image" && image.crop) || isLocked(doc, id)
        },
        { label: "Calibrate from this image", action: () => this.switchTool("calibrate") },
        { label: "Lock as underlay", action: () => this.lockAsUnderlay(id), disabled: isLocked(doc, id) }
      );
    }

    // polyline point surgery — exactly one polyline selected
    if (sel.length === 1 && doc.entities[sel[0]!]?.type === "polyline" && !isLocked(doc, sel[0]!)) {
      const id = sel[0]!;
      const entity = doc.entities[id]!;
      const vHit = vertexAt(doc, id, world, 8 / scale);
      const removeCmd = vHit ? removeVertexCommand(doc, vHit) : null;
      const addCmd = addVertexCommand(doc, id, world, HIT_TOL_PX / scale);
      const simplifyCmd = simplifyCommand(doc, id, 2 / scale);
      const closeCmd = toggleClosedCommand(doc, id);
      items.push(
        { label: "Add point here", action: () => this.dispatchAndRender(addCmd), disabled: !addCmd },
        { label: "Remove point", action: () => this.dispatchAndRender(removeCmd), disabled: !removeCmd },
        {
          label: "Simplify points",
          action: () => this.dispatchAndRender(simplifyCmd),
          disabled: !simplifyCmd
        },
        {
          label: entity.type === "polyline" && entity.closed ? "Open shape" : "Close shape",
          action: () => this.dispatchAndRender(closeCmd),
          disabled: !closeCmd
        }
      );
    }

    const anyLocked = sel.some((id) => isLocked(doc, id));
    const anyUnlockedEntity = sel.some((id) => doc.entities[id]?.locked);
    const hasGroup = sel.some((id) => doc.entities[id]?.type === "group" && !wallMetaOf(doc.entities[id]));
    const singleStyled = sel.length === 1 && doc.entities[sel[0]!] && "style" in doc.entities[sel[0]!]!;

    items.push(
      { label: "Cut", separator: items.length > 0, action: () => this.cutSelection(), disabled: anyLocked },
      { label: "Copy", action: () => this.copySelection() },
      { label: "Paste", action: () => this.paste(), disabled: this.clipboard.entities.length === 0 },
      { label: "Duplicate", action: () => this.duplicateSelection() },
      { label: "Delete", action: () => this.deleteSelection(), disabled: anyLocked },
      {
        label: "Move to layer",
        separator: true,
        children: doc.layers.map((l) => ({
          label: l.name,
          action: () => this.moveSelectionToLayer(l.id),
          disabled: sel.every((id) => doc.entities[id]?.layerId === l.id)
        }))
      },
      { label: "Move by… (M)", separator: true, action: () => this.openPrompt("move"), disabled: anyLocked },
      { label: "Rotate by… (Q)", action: () => this.openPrompt("rotate"), disabled: anyLocked },
      { label: "Save to library… (B)", action: () => this.openPrompt("save") },
      { label: "Group", separator: true, action: () => this.groupSelection(), disabled: sel.length < 2 || anyLocked },
      { label: "Ungroup", action: () => this.ungroupSelection(), disabled: !hasGroup },
      {
        label: "Order",
        separator: true,
        children: [
          { label: "Bring to front", action: () => this.reorderSelection("front") },
          { label: "Bring forward", action: () => this.reorderSelection("forward") },
          { label: "Send backward", action: () => this.reorderSelection("backward") },
          { label: "Send to back", action: () => this.reorderSelection("back") }
        ]
      },
      anyUnlockedEntity || anyLocked
        ? { label: "Unlock", action: () => this.lockSelection(false) }
        : { label: "Lock", action: () => this.lockSelection(true) },
      {
        label: "Copy style",
        separator: true,
        action: () => this.copyStyle(),
        disabled: !singleStyled
      },
      { label: "Paste style", action: () => this.pasteStyle(), disabled: !this.copiedStyle || anyLocked }
    );
    return items;
  }

  selectAll(): void {
    const doc = this.store.doc;
    const ids: string[] = [];
    for (const layer of doc.layers) {
      if (!layer.visible || layer.locked) continue;
      for (const id of doc.zOrder[layer.id] ?? []) {
        const entity = doc.entities[id];
        if (entity && !entity.hidden && !entity.locked) ids.push(id);
      }
    }
    this.selection.set(ids);
    this.viewport.scheduler.invalidate("overlay");
  }

  /** TL-4: Measurements commit. Returns false → EC-DM-5 invalid flash. */
  measure(raw: string): boolean {
    if (this.measureShape !== "none") {
      const ok = this.active.measurement(raw);
      if (ok) this.notify();
      return ok;
    }
    // post-commit re-edit window: 10 s (TL-4)
    if (this.reapply && Date.now() - this.reapplyAt < 10_000) {
      return this.reapply(raw);
    }
    return false;
  }

  // ---- ctx ------------------------------------------------------------------

  get activeLayerId(): string {
    const id = this.activeLayer;
    if (id && this.store.doc.layers.some((l) => l.id === id)) return id;
    return defaultActiveLayerId(this.store.doc);
  }

  setActiveLayer(id: string): void {
    this.activeLayer = id;
    this.notify();
  }

  private buildCtx(): ToolCtx {
    return {
      store: this.store,
      selection: this.selection,
      index: this.viewport.index,
      assets: this.viewport.assets,
      switchTool: (id) => this.switchTool(id),
      activeLayerId: () => this.activeLayerId,
      scale: () => this.viewport.camera.current.scale,
      minorStep: () => gridStep(this.viewport.camera.current.scale),
      docUnits: () => ({ units: this.store.doc.units, precision: this.store.doc.precision }),
      requestRender: () => this.viewport.scheduler.invalidate("dynamic", "overlay"),
      setEditing: (ids) => this.viewport.setEditing(ids),
      setHint: (text) => {
        this.hint = text;
        this.notify();
      },
      setMeasure: (shape, ghost) => {
        this.measureShape = shape;
        this.measureGhost = ghost;
        this.notify();
      },
      setSnapBase: (p) => {
        this.snapBase = p;
      },
      setSelfClose: (p) => {
        this.selfClose = p;
      },
      registerReapply: (fn) => {
        this.reapply = fn;
        this.reapplyAt = Date.now();
      },
      gestureActive: (active) => {
        this.isGestureActive = active;
      },
      requestTextEdit: (req) => this.openTextEdit(req),
      prefs: () => ({
        freehandTolPx: this.prefs.freehandTolPx,
        polar45: this.prefs.polar45,
        highlighter: this.prefs.highlighter,
        pipe: this.prefs.pipe,
        wall: this.prefs.wall
      }),
      addGuide: (origin, dir) => this.viewport.addGuide(origin, dir),
      resolveSnap: (p) =>
        this.snap.resolve({
          cursor: p,
          scale: this.viewport.camera.current.scale,
          doc: this.store.doc,
          index: this.viewport.index,
          excludeIds: this.viewport.editingIds,
          basePoint: null,
          selfClosePoint: null,
          guides: this.viewport.guides,
          shift: false,
          alt: this.lastMods.alt,
          now: performance.now()
        }),
      setCursor: (cursor) => this.viewport.setCursor(cursor ?? TOOL_CURSOR[this.active.id] ?? null),
      setCursorTip: (text) => {
        if (text === this.cursorTip) return;
        this.cursorTip = text;
        this.viewport.scheduler.invalidate("overlay");
      },
      selectedDoor: () => this.selectedDoor,
      selectDoor: (pick) => this.selectDoor(pick)
    };
  }

  clearGuides(): void {
    this.viewport.clearGuides();
    this.setHint("Guides cleared");
  }

  // ---- inline text editing (ANN-1) ---------------------------------------------

  private openTextEdit(req: TextEditRequest): void {
    const vp = this.viewport;
    const screen = worldToScreen(vp.camera.current, vp.scheduler.width, vp.scheduler.height, req.world);
    this.textEditId++;
    this.textEdit = {
      world: req.world,
      fontMm: req.fontMm,
      screen,
      fontPx: Math.max(11, req.fontMm * vp.camera.current.scale), // EC-ANN-1 minimum
      initial: req.initial,
      multiline: req.multiline
    };
    this.textEditCommit = req.onCommit;
    this.textEditCancel = req.onCancel ?? null;
    this.viewport.setCursor("text"); // edit mode is visible at the pointer too
    this.notify();
  }

  /** UI calls this on commit (text) or cancel (null). */
  finishTextEdit(text: string | null): void {
    const commit = this.textEditCommit;
    const cancel = this.textEditCancel;
    this.textEdit = null;
    this.textEditCommit = null;
    this.textEditCancel = null;
    if (text !== null) commit?.(text);
    else cancel?.();
    this.viewport.setCursor(TOOL_CURSOR[this.active.id] ?? null);
    this.viewport.scheduler.invalidate("static", "overlay");
    this.notify();
  }

  /** F2 / double-click: edit an existing text or label; empty commits delete (EC-TL-14). */
  editEntityText(id: string): void {
    const doc = this.store.doc;
    const entity = doc.entities[id];
    if (!entity || (entity.type !== "text" && entity.type !== "label") || isLocked(doc, id)) return;
    const world = entity.type === "text" ? entity.pos : entity.tail;
    this.openTextEdit({
      world,
      fontMm: entity.textStyle.size,
      initial: entity.content,
      multiline: entity.type === "text",
      onCommit: (text) => {
        if (text.trim().length === 0) {
          this.store.dispatch(makeDeleteCommand(doc, [id]));
          this.selection.clear();
        } else if (text !== entity.content) {
          this.store.dispatch({
            kind: "updateEntities",
            patches: [{ id, before: { content: entity.content }, after: { content: text } }]
          });
        }
      }
    });
  }

  // ---- viewport bridge --------------------------------------------------------

  private wireViewport(): void {
    const vp = this.viewport;
    vp.delegate = {
      pointerDown: (screen, mods, button) => {
        if (this.pickingPivot && button === 0) {
          // the click sets the turning point (snapped); the tool never sees it
          const id = this.pickingPivot;
          const ev = this.buildEvent(screen, mods, button);
          this.pickingPivot = null;
          if (this.store.doc.entities[id] && this.setPivot(id, ev.point)) {
            this.setHint("Turning point set. Q, the Rotation box and the rotate knob now turn about it.");
          }
          this.notify();
          return;
        }
        if (this.pickingAlignRef && button === 0) {
          // the click picks the reference object; the tool never sees it
          const world = vp.cursorWorld(screen);
          const hit = hitTest({ doc: this.store.doc, index: vp.index }, world, HIT_TOL_PX / vp.camera.current.scale);
          if (hit) {
            this.alignRef = hit;
            this.pickingAlignRef = false;
            this.setHint("Align to: reference object set. Press an align button.");
            vp.scheduler.invalidate("overlay");
            this.notify();
          } else {
            this.setHint("Align to: that spot is empty. Click an object, or press Esc.");
          }
          return;
        }
        if (button === 2 && this.active.inProgress()) {
          // TL-2: a right-click finishes a multi-point input and shows no menu. 3.11.0: in the Pipe tool, a right-click
          // on a free pipe end (the end of the pipe just drawn) ends the run and opens the menu of that end.
          const onEnd = this.active.id === "pipe" ? this.freeEndAt(screen, 24) : null;
          if (!onEnd || onEnd.ownerKind !== "pipe") this.rightClickConsumed = true;
        }
        this.active.pointerDown(this.buildEvent(screen, mods, button));
      },
      pointerMove: (screen, mods) => {
        this.lastScreen = screen;
        this.lastMods = mods;
        const e = this.buildEvent(screen, mods, -1);
        this.active.pointerMove(e);
        this.autoscroll(screen, mods);
        vp.scheduler.invalidate("overlay");
        this.notify();
      },
      pointerUp: (screen, mods, button) => this.active.pointerUp(this.buildEvent(screen, mods, button)),
      doubleClick: (screen, mods) => {
        // TL-5: dblclick text/label = edit, image = perspective; otherwise the tool decides
        if (this.active.id === "select") {
          const world = vp.cursorWorld(screen);
          const hit = hitTest(
            { doc: this.store.doc, index: vp.index },
            world,
            HIT_TOL_PX / vp.camera.current.scale
          );
          const entity = hit ? this.store.doc.entities[hit] : undefined;
          if (entity?.type === "text" || entity?.type === "label") {
            this.selection.set([entity.id]);
            this.editEntityText(entity.id);
            return;
          }
          if (entity?.type === "image" && !isLocked(this.store.doc, entity.id)) {
            this.startPerspective(entity.id);
            return;
          }
        }
        this.active.doubleClick(this.buildEvent(screen, mods, 0));
      },
      key: (e) => this.handleKey(e),
      wheel: (_screen: Vec, deltaY: number, mods: PointerMods) => {
        const used = this.active.wheel?.({ deltaY, shift: mods.shift, ctrl: mods.ctrl, alt: mods.alt }) ?? false;
        if (used) {
          this.viewport.scheduler.invalidate("dynamic", "overlay");
          this.notify();
        }
        return used;
      },
      contextMenu: (screen: Vec, _mods: PointerMods) => {
        const items = this.buildContextMenu(screen);
        if (items && items.length > 0) this.onContextMenu?.(screen, items);
        this.viewport.scheduler.invalidate("overlay");
        this.notify();
      }
    };
    vp.setDynamicRenderer((view) => {
      clearReadoutBoxes(view);
      applyWorldTransform(view);
      this.active.renderDynamic?.(view);
    });
    vp.setOverlayRenderer((view) => this.renderOverlay(view));
  }

  private buildEvent(
    screen: Vec,
    mods: { shift: boolean; ctrl: boolean; alt: boolean },
    button: number
  ): ToolPointerEvent {
    const vp = this.viewport;
    const world = vp.cursorWorld(screen);
    const snap = this.snap.resolve({
      cursor: world,
      scale: vp.camera.current.scale,
      doc: this.store.doc,
      index: vp.index,
      excludeIds: vp.editingIds,
      basePoint: this.snapBase,
      selfClosePoint: this.selfClose,
      guides: this.viewport.guides,
      shift: mods.shift,
      alt: mods.alt,
      now: performance.now(),
      noOrtho: this.active.ignoresOrtho?.() ?? false
    });
    this.lastSnap = snap;
    return { world, point: snap.point, snap, screen, ...mods, button, held: vp.heldKeys };
  }

  private handleKey(e: { key: string; ctrl: boolean; shift: boolean; alt: boolean }): boolean {
    if (e.key === "Escape" && this.pickingPivot) {
      this.cancelPickPivot();
      return true;
    }
    if (e.key === "Escape" && this.pickingAlignRef) {
      this.cancelPickAlignRef();
      return true;
    }
    if (e.key === "[" || e.key === "]") {
      return this.adjustImageOpacity(e.key === "[" ? -0.1 : 0.1); // IMG-12
    }
    if (e.key === "F2") {
      const sel = [...this.selection.all];
      if (sel.length === 1) {
        this.editEntityText(sel[0]!);
        return true;
      }
    }
    if (e.key === "Tab") {
      this.snap.cycleTab();
      // re-resolve at the same cursor so the marker/point update immediately
      this.active.pointerMove(this.buildEvent(this.lastScreen, this.lastMods, -1));
      this.viewport.scheduler.invalidate("overlay");
      return true;
    }
    if (!e.ctrl && e.key === " ") {
      return false; // Space handled by the viewport (pan/repeat)
    }
    if (e.ctrl) {
      const combo = e.key.toLowerCase();
      if (combo === "a") {
        this.selectAll(); // Ctrl+A: all visible, unlocked (TL-2)
        return true;
      }
      if (combo === "c") {
        this.copySelection();
        return true;
      }
      if (combo === "x") {
        this.cutSelection();
        return true;
      }
      if (combo === "v") {
        this.paste();
        return true;
      }
      if (combo === "d") {
        this.duplicateSelection();
        return true;
      }
      if (combo === "g") {
        if (e.shift) this.ungroupSelection();
        else this.groupSelection();
        return true;
      }
    }
    if (this.active.key(e)) {
      this.viewport.scheduler.invalidate("dynamic", "overlay");
      return true;
    }
    // M = move by, Q = rotate by (SketchUp's keys), B = save to library — on a selection.
    if (!e.ctrl && !e.alt && this.active.id === "select" && this.selection.size > 0) {
      const k = e.key.toLowerCase();
      const prompt: SelectionPrompt | null = k === "m" ? "move" : k === "q" ? "rotate" : k === "b" ? "save" : null;
      if (prompt && !this.prefs.keymap[k]) { // a key the user mapped to a tool stays that tool
        this.openPrompt(prompt);
        return true;
      }
    }
    const toolId = !e.ctrl && !e.alt ? this.prefs.keymap[e.key.toLowerCase()] : undefined;
    if (toolId) {
      this.switchTool(toolId);
      return true;
    }
    if (e.key === "Escape") {
      this.active.cancel();
      return true;
    }
    return false;
  }

  /** EC-TL-18 minimal autoscroll: pan toward the edge during an active gesture. */
  private autoscroll(screen: Vec, mods: { shift: boolean; ctrl: boolean; alt: boolean }): void {
    void mods;
    if (!this.isGestureActive) return;
    const vp = this.viewport;
    const M = 24;
    const dx = screen.x < M ? -10 : screen.x > vp.scheduler.width - M ? 10 : 0;
    const dy = screen.y < M ? -10 : screen.y > vp.scheduler.height - M ? 10 : 0;
    if (dx !== 0 || dy !== 0) vp.panScreen(-dx, -dy);
  }

  // ---- overlay -------------------------------------------------------------

  private startPulse(ids: string[]): void {
    const already = this.pulse !== null;
    this.pulse = { ids, until: performance.now() + 450 };
    if (already) return; // the running rAF chain picks up the new pulse
    const tick = (): void => {
      if (!this.pulse) return;
      this.viewport.scheduler.invalidate("overlay");
      if (performance.now() < this.pulse.until) requestAnimationFrame(tick);
      else {
        this.pulse = null;
        this.viewport.scheduler.invalidate("overlay");
      }
    };
    requestAnimationFrame(tick);
  }

  private renderOverlay(v: RenderView): void {
    clearReadoutBoxes(v);
    applyWorldTransform(v);
    const doc = this.store.doc;
    const px = (n: number): number => n / v.cam.scale;

    // fading accent pulse on freshly created entities
    if (this.pulse) {
      const a = Math.max(0, Math.min(1, (this.pulse.until - performance.now()) / 450));
      const accent =
        getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#4dd0e1";
      v.ctx.globalAlpha = a * 0.9;
      for (const id of this.pulse.ids) outlineEntity(v, doc, id, accent, px(3));
      v.ctx.globalAlpha = 1;
    }

    // ANN-7 guides: infinite dashed construction lines
    if (this.viewport.guides.length > 0) {
      const far = 1e6;
      v.ctx.strokeStyle = "#c58af8";
      v.ctx.lineWidth = px(0.75);
      v.ctx.setLineDash([px(8), px(6)]);
      for (const g of this.viewport.guides) {
        v.ctx.beginPath();
        v.ctx.moveTo(g.origin.x - g.dir.x * far, g.origin.y - g.dir.y * far);
        v.ctx.lineTo(g.origin.x + g.dir.x * far, g.origin.y + g.dir.y * far);
        v.ctx.stroke();
      }
      v.ctx.setLineDash([]);
    }

    // the object the selection lines up with: dashed orange box
    const refBox = this.alignRefBox();
    if (refBox) {
      const pad = px(4);
      v.ctx.strokeStyle = "#f0a030";
      v.ctx.lineWidth = px(2);
      v.ctx.setLineDash([px(8), px(4)]);
      v.ctx.strokeRect(refBox.minX - pad, refBox.minY - pad, refBox.maxX - refBox.minX + pad * 2, refBox.maxY - refBox.minY + pad * 2);
      v.ctx.setLineDash([]);
    }

    // selection highlight (always visible, any tool)
    const live = this.active.id === "select" ? (this.active as SelectTool).drawnLive() : null;
    for (const id of this.selection.all) {
      if (id === live) continue; // 3.21.0: a wall whose door is dragged is drawn by the Select tool (renderDynamic)
      outlineEntity(v, doc, id, "#4d9fe1", px(2));
      // groups get a distinct dashed bounding box so grouping is visibly different
      const entity = doc.entities[id];
      if (entity?.type === "group") {
        const b = entityBounds(entity, doc);
        if (b.minX <= b.maxX) {
          const pad = px(6);
          v.ctx.strokeStyle = "#4d9fe1";
          v.ctx.lineWidth = px(1.5);
          v.ctx.setLineDash([px(6), px(4)]);
          v.ctx.strokeRect(b.minX - pad, b.minY - pad, b.maxX - b.minX + pad * 2, b.maxY - b.minY + pad * 2);
          v.ctx.setLineDash([]);
        }
      }
    }

    // pipe and fitting ends of the one selected object: ring = free, filled = a pipe is attached
    if (this.selection.size === 1) {
      const only = [...this.selection.all][0]!;
      const entity = doc.entities[only];
      if (looksLikeFitting(entity) || looksLikePipe(entity)) drawPorts(v, safePorts(doc, only));
    }

    this.active.renderOverlay?.(v);

    // inference rays (SNP-5/SNP-6)
    const snap = this.lastSnap;
    if (snap) {
      const css = getComputedStyle(document.documentElement);
      const colors = {
        x: css.getPropertyValue("--axis-x").trim() || "#e05c5c",
        y: css.getPropertyValue("--axis-y").trim() || "#4caf7d",
        snap: css.getPropertyValue("--snap").trim() || "#e0b34d"
      };
      for (const ray of snap.rays) {
        v.ctx.strokeStyle = colors[ray.color];
        v.ctx.lineWidth = px(1);
        v.ctx.setLineDash([px(5), px(4)]);
        v.ctx.beginPath();
        v.ctx.moveTo(ray.origin.x, ray.origin.y);
        v.ctx.lineTo(snap.point.x, snap.point.y);
        v.ctx.stroke();
        v.ctx.setLineDash([]);
      }
      // snap marker (SNP-6, 7 px screen glyphs)
      if (snap.kind !== "free" && snap.kind !== "ortho") {
        drawSnapMarker(v, snap);
      }
      // charged point pulses
      for (const c of this.snap.chargedPoints) {
        v.ctx.strokeStyle = colors.snap;
        v.ctx.lineWidth = px(1);
        v.ctx.beginPath();
        v.ctx.arc(c.p.x, c.p.y, px(4), 0, Math.PI * 2);
        v.ctx.stroke();
      }
    }

    // committed-point marker: while a multi-step tool waits for its next click, the
    // last committed point (= the snap base) gets a visible anchor dot
    if (this.snapBase && this.active.inProgress()) {
      const s = worldToScreen(v.cam, v.cssW, v.cssH, this.snapBase);
      const { ctx } = v;
      ctx.save();
      ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
      const accent =
        getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#4dd0e1";
      ctx.fillStyle = accent;
      ctx.strokeStyle = accent;
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Alt suspends snapping — say so at the pointer instead of letting markers just vanish
    const tip =
      this.lastMods.alt && this.snap.enabled
        ? this.cursorTip
          ? `${this.cursorTip} · snap off`
          : "snap off"
        : this.cursorTip;
    if (tip) drawCursorTip(v, this.lastScreen, tip);
  }
}

/** Screen-space step prompt beside the pointer (kept short; the status bar has the long hint). */
function drawCursorTip(v: RenderView, screen: Vec, text: string): void {
  const { ctx } = v;
  ctx.save();
  ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
  ctx.font = "12px Inter, system-ui, sans-serif";
  const padX = 6;
  const h = 20;
  const w = ctx.measureText(text).width + padX * 2;
  // A size or length readout sits at the pointer too (line.ts drawReadout): the tip never covers it (3.10.1).
  const { x, y } = placeCursorTip(screen, w, h, v.cssW, v.cssH, drawnReadoutBoxes());
  const css = getComputedStyle(document.documentElement);
  ctx.fillStyle = css.getPropertyValue("--panel").trim() || "#22262f";
  ctx.strokeStyle = css.getPropertyValue("--panel-border").trim() || "#2e3340";
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.92;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 4);
  ctx.fill();
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = css.getPropertyValue("--text").trim() || "#e8eaf0";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + h / 2 + 0.5);
  ctx.restore();
}

function drawSnapMarker(v: RenderView, snap: SnapResult): void {
  const { ctx } = v;
  const s = worldToScreen(v.cam, v.cssW, v.cssH, snap.point);
  ctx.save();
  ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
  ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue("--snap").trim() || "#e0b34d";
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  switch (snap.kind) {
    case "endpoint":
      ctx.rect(s.x - r, s.y - r, r * 2, r * 2);
      break;
    case "midpoint":
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.lineTo(s.x - r, s.y + r);
      ctx.closePath();
      break;
    case "center":
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      break;
    case "intersection":
      ctx.moveTo(s.x - r, s.y - r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.moveTo(s.x + r, s.y - r);
      ctx.lineTo(s.x - r, s.y + r);
      break;
    case "quadrant":
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x + r, s.y);
      ctx.lineTo(s.x, s.y + r);
      ctx.lineTo(s.x - r, s.y);
      ctx.closePath();
      break;
    case "grid":
      ctx.rect(s.x - r, s.y - r, r * 2, r * 2);
      ctx.moveTo(s.x - r, s.y);
      ctx.lineTo(s.x + r, s.y);
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x, s.y + r);
      break;
    default:
      ctx.moveTo(s.x - r, s.y);
      ctx.lineTo(s.x + r, s.y);
      break;
  }
  ctx.stroke();
  ctx.restore();
}
